import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useState } from 'react';
import { Menu, MenuItem, ListItemIcon, ListItemText, Box, useTheme, Divider, IconButton } from '@mui/material';
import useMenuStyles, { GroupedMenuItem, UngroupedMenuItem } from './useMenuStyles';
import { useLocation, useNavigate } from 'react-router-dom';
import { centeredColumnStack } from '@ui/style/style';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
export const StyledMenu = ({ actions, onClose, selectedItem, setSelectedItem, menuVariant = 'light', // Default to light for backward compatibility
searchField, ...menuProps }) => {
    const location = useLocation();
    const isCheckboxAction = (action) => {
        if (action.isCheckbox)
            return true;
        if (typeof action.icon === 'object' && action.icon) {
            // Check if it's a React element with Checkbox type
            const element = action.icon;
            return (element.type?.displayName === 'Checkbox' ||
                element.type?.name === 'Checkbox' ||
                element.props?.type === 'checkbox' ||
                (element.type && element.type.toString().includes('Checkbox')));
        }
        return false;
    };
    const isSwitchAction = (action) => {
        if (action.isSwitch)
            return true;
        if (typeof action.icon === 'object' && action.icon) {
            // Check if it's a React element with Switch type
            const element = action.icon;
            return (element.type?.displayName === 'Switch' ||
                element.type?.name === 'Switch' ||
                element.props?.type === 'switch' ||
                (element.type && element.type.toString().includes('Switch')));
        }
        return false;
    };
    const hasCheckboxActions = actions.some((action) => isCheckboxAction(action));
    const groupedItems = [];
    let currentGroup = [];
    const navigate = useNavigate();
    const selectedPath = location.pathname;
    const styles = useMenuStyles(menuVariant); // Pass menuVariant to styles
    const theme = useTheme();
    actions.forEach((action) => {
        if (action.group) {
            currentGroup.push(action);
        }
        else {
            if (currentGroup.length)
                groupedItems.push(currentGroup);
            groupedItems.push([action]);
            currentGroup = [];
        }
    });
    if (currentGroup.length)
        groupedItems.push(currentGroup);
    const [submenuAnchorEl, setSubmenuAnchorEl] = useState(null);
    const [openSubmenu, setOpenSubmenu] = useState(null);
    return (_jsx(_Fragment, { children: _jsxs(Menu, { ...menuProps, onClose: onClose, slotProps: {
                paper: {
                    sx: {
                        ...styles.mainMenuTag
                    }
                },
                list: {
                    disablePadding: true
                }
            }, children: [searchField && searchField, _jsx(Box, { children: groupedItems.map((group, groupIndex) => {
                        return (_jsxs(React.Fragment, { children: [groupIndex > 0 && (_jsx(Divider, { sx: {
                                        backgroundColor: '#919EAB33',
                                        margin: '0px auto',
                                        width: '97%',
                                        height: '1px'
                                    } })), _jsx(Box, { sx: {
                                        ...centeredColumnStack,
                                        // mb: groupIndex < groupedItems.length - 1 ? 1 : 0,
                                        overflow: 'hidden'
                                        // borderRadius: theme.spacing(1)
                                    }, children: group.map((action, itemIndex) => {
                                        const isGrouped = group.length > 1;
                                        const isCheckbox = isCheckboxAction(action);
                                        const isSwitch = isSwitchAction(action);
                                        return (_jsx(React.Fragment, { children: _jsxs(MenuItem, { disabled: action.disabled, selected: (action.id
                                                    ? action.id === selectedItem
                                                    : action.text === selectedItem) ||
                                                    (action.to ? action.to === selectedPath : false), onClick: (event) => {
                                                    if (action.children) {
                                                        event.stopPropagation();
                                                        setSubmenuAnchorEl(event.currentTarget);
                                                        setOpenSubmenu(action.children);
                                                        return;
                                                    }
                                                    if (setSelectedItem) {
                                                        setSelectedItem(action.id ?? action.text);
                                                    }
                                                    if (action.keepOpen) {
                                                        event.stopPropagation();
                                                    }
                                                    else {
                                                        onClose && onClose();
                                                    }
                                                    if (typeof action.to === 'function')
                                                        action.to(event);
                                                    else if (typeof action.to === 'string')
                                                        navigate(action.to);
                                                }, sx: {
                                                    ...(isGrouped
                                                        ? GroupedMenuItem(theme, itemIndex === 0, menuVariant)
                                                        : UngroupedMenuItem(theme, menuVariant)),
                                                    width: '100%',
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'center',
                                                    paddingLeft: isSwitch ? 0 : undefined
                                                }, children: [_jsxs(Box, { sx: {
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            py: isCheckbox ? '6px' : undefined
                                                        }, children: [action.icon && (_jsx(ListItemIcon, { sx: {
                                                                    ...styles.menuIcon,
                                                                    ...(action.color && { color: action.color }),
                                                                    alignSelf: 'center'
                                                                }, children: typeof action.icon === 'string' ? (_jsx("img", { src: action.icon, alt: '' })) : (action.icon) })), _jsx(ListItemText, { slotProps: {
                                                                    primary: {
                                                                        sx: {
                                                                            ...styles.menuText,
                                                                            ...(action.color && { color: action.color || 'textPrimary' })
                                                                        }
                                                                    },
                                                                    secondary: action.secondaryText
                                                                        ? {
                                                                            sx: {
                                                                                color: (theme) => theme.palette.text.secondary,
                                                                                fontSize: '0.75rem',
                                                                                lineHeight: 1.2,
                                                                                mt: '4px'
                                                                            }
                                                                        }
                                                                        : undefined
                                                                }, primary: action.text, secondary: action.secondaryText })] }), _jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 0.5 }, children: [action.endIcon && (_jsx(IconButton, { disableRipple: true, disableFocusRipple: true, onClick: (event) => {
                                                                    event.stopPropagation();
                                                                    action.onEndIconClick?.(event);
                                                                }, sx: {
                                                                    width: 20,
                                                                    height: 20,
                                                                    border: 'none',
                                                                    color: action.color || 'inherit',
                                                                    padding: 0.5
                                                                }, children: typeof action.endIcon === 'string' ? (_jsx("img", { src: action.endIcon, alt: '', style: { width: 16, height: 16 } })) : (action.endIcon) })), action.children && (_jsx(KeyboardArrowRightIcon, { fontSize: 'small', sx: { ml: 1, color: action.color || 'inherit' } }))] })] }) }, `group-${groupIndex}-item-${itemIndex}-${action.id || action.text}`));
                                    }) })] }, `group-${groupIndex}`));
                    }) }), openSubmenu && submenuAnchorEl && (_jsx(StyledMenu, { anchorEl: submenuAnchorEl, anchorOrigin: { vertical: 'top', horizontal: 'left' }, transformOrigin: { vertical: 'top', horizontal: 'right' }, open: Boolean(openSubmenu), onClose: () => {
                        setOpenSubmenu(null);
                        setSubmenuAnchorEl(null);
                    }, actions: openSubmenu, selectedItem: selectedItem, setSelectedItem: setSelectedItem, menuVariant: menuVariant }))] }) }));
};
